import { type Registry as Services, service } from '@ember/service';
import Model, { attr, belongsTo } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import type { Carate, PaRecommendationReason } from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import type { HoteldateTransformReturn } from 'lumen/transforms/hoteldate';
import {
  RECOMMENDATION_ACTION_STATES,
  RECOMMENDATION_RATE_DIFFERENCE_FORMATS,
} from 'lumen/utils/constants/pricing-assistant-constants';
import { formatDifferenceBetweenRecommendationAndOwnRate } from 'lumen/utils/pricing-assistant/pricing-assistant-formatting';

export interface Factor {
  compset: number;
  market: number;
  occupancy: number;

  user_rules: number;
}

export default class Carecommendation extends Model {
  [Type] = 'carecommendation' as const;

  // Belongs to a carate model object (every rate has one recommendation for now).
  @belongsTo('carate', { async: false, inverse: 'carecommendation' })
  declare carate: Carate;

  @belongsTo('pa-recommendation-reason', {
    async: false,
    inverse: 'carecommendation',
  })
  declare paRecommendationReason: PaRecommendationReason;

  @attr('string') declare action: string;
  /** The recommended value (on the OTA) the system would have produced if no override were active.
    Only present when an override replaced the natural recommendation; null otherwise. */
  @attr('number') declare actualRecommendedValue: number;
  // Boolean indicating if autopilot is enabled for this recommendation (will be false if Autopilot isn't supported by the hotel)
  @attr('boolean') declare autopilotEnabled: boolean;
  @attr('string') declare bulkPushId: string | null;
  @attr('string') declare currency: string;
  @attr('number') declare currentValue: number | null;
  @attr() declare factors: Factor;
  @attr('hoteldate') declare fromDate: NonNullable<HoteldateTransformReturn>;
  @attr('number') declare overwrittenValue: number | null;
  @attr('string') declare pmsRoomtype: string;
  // Message indicating the cause of Push to PMS exception
  @attr('string') declare pushResultMessage: string;
  // Property that might contain the id of a recommendationAction (if the recommendation has been actioned)
  @attr('number') declare recommendationActionId: number;
  /* BAR discount applied to the recommended price on dynamic rate views (decimal, e.g. 0.05 = 5%). Null on BAR. */
  @attr('number') declare recommendedDiscount: number | null;
  @attr('number') declare recommendedPmsValue: number;
  @attr('number') declare recommendedValue: number;

  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];

  get formattedActualRecommendedValue(): string | null {
    if (this.recommendedValue) {
      return this.hotelAndMarketService.formatRate(
        this.actualRecommendedValue,
        { longFormat: false },
      );
    }

    return this.intl.t('general.missing');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedCurrentValue() {
    if (this.currentValue) {
      return this.hotelAndMarketService.formatRate(this.currentValue, {
        longFormat: false,
      });
    }

    return this.intl.t('general.missing');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedDifferenceBetweenRecommendationAndOwnRate() {
    if (this.carate && this.carate.hasValue) {
      // We always show relative difference for now
      return formatDifferenceBetweenRecommendationAndOwnRate(
        this.intl,
        this.priceToShow,
        this.carate.value,
        RECOMMENDATION_RATE_DIFFERENCE_FORMATS.RELATIVE,
        this.hotelAndMarketService,
      );
    }

    return null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedOverwrittenValue() {
    if (this.overwrittenValue) {
      return this.hotelAndMarketService.formatRate(this.overwrittenValue, {
        longFormat: false,
      });
    }

    return this.intl.t('general.missing');
  }

  // Same as above, but already formatted
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedPriceToShow() {
    if (this.overwrittenValue) {
      return this.formattedOverwrittenValue;
    }

    return this.formattedRecommendedValue;
  }

  get formattedRecommendedDiscount(): string | null {
    if (this.recommendedDiscount == null) {
      return null;
    }
    return this.intl.formatNumber(this.recommendedDiscount, {
      style: 'percent',
      maximumFractionDigits: 0,
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedRecommendedPmsValue() {
    if (this.recommendedPmsValue) {
      return this.hotelAndMarketService.formatRate(this.recommendedPmsValue, {
        longFormat: false,
      });
    }

    return this.intl.t('general.missing');
  }

  get formattedRecommendedValue(): string | null {
    if (this.recommendedValue) {
      return this.hotelAndMarketService.formatRate(this.recommendedValue, {
        longFormat: false,
      });
    }

    return this.intl.t('general.missing');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasException() {
    return this.action === RECOMMENDATION_ACTION_STATES.EXCEPTION;
  }

  // API only sends carate id in carecommendations. Carates have no extra property linking to a carecommendation

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isAccepted() {
    return this.action === RECOMMENDATION_ACTION_STATES.ACCEPTED;
  }

  // Extra property that we can check in FE to see if a recommendation is in the progress "being pushed". Allows us to give more visual feedback to the user
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isBeingPushed() {
    return this.isAccepted || this.isPushing;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isBeingPushedOrIsPushed() {
    return this.isBeingPushed || this.isPushed;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isPushed() {
    return this.action === RECOMMENDATION_ACTION_STATES.PUSHED;
  }

  // Note: this is mainly a BE state. We can't really use it for showing a "loading"/"handling" UI after accepting a recommendation & waiting for the result
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isPushing() {
    return this.action === RECOMMENDATION_ACTION_STATES.PUSHING;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isRecommendedPriceOverwritten() {
    return this.overwrittenValue !== null;
  }

  // In some cases we'll show only one price to users. That'll be the overwritten price if it exists, otherwise the price we've recommended
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get priceToShow() {
    if (this.overwrittenValue) {
      return this.overwrittenValue;
    }

    return this.recommendedValue;
  }

  // Show recommendationActionState component in case a recommendation has an action state except the exception one
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showRecommendationActionState() {
    return this.action && !this.hasException;
  }

  // To log the carecommendation object in the console, we need to use the toJSON method, otherwise we get the proxy object
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  toJSON() {
    return this.serialize();
  }
}
